import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" onClick={closeMobile}>
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
              Sugargoo Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1.5">
            <Link
              to="/"
              className="px-4 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
            >
              Home
            </Link>
            <a
              href="#guides"
              className="px-4 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
            >
              Shopping Guide
            </a>
            <a
              href="#categories"
              className="px-4 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
            >
              Categories
            </a>
            <a
              href="#articles"
              className="px-4 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
            >
              Articles
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl text-foreground-900 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={closeMobile}></div>
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-4 py-6 space-y-6">
              <div>
                <Link
                  to="/"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200/60 transition-colors cursor-pointer mb-2"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/70">
                    <i className="ri-home-4-line text-primary-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Home</span>
                </Link>
                <a
                  href="#guides"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200/60 transition-colors cursor-pointer mb-2"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100/70">
                    <i className="ri-book-open-line text-accent-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Shopping Guide</span>
                </a>
                <a
                  href="#articles"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200/60 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100/70">
                    <i className="ri-article-line text-secondary-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">SEO Articles</span>
                </a>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-1 mb-3">Product Categories</h3>
                <div className="space-y-1">
                  {categories.map((cat, idx) => (
                    <a
                      key={idx}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobile}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100/60 flex-shrink-0">
                        <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                      </div>
                      <span className="text-sm text-foreground-700 whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3.5 text-base font-semibold text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Sugargoo Spreadsheet &mdash; Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}